import { useEffect, useMemo, useRef, useState } from "react";
import { parseFilterInputs } from "./filterInputs";
import { detectBrowserTimeZone } from "./localInstantToUtc";

/** PRD-001 rev 13: the resolved filter a submission carries. */
export interface ResolvedFilter {
    createdStart?: string;
    label?: string;
}

interface DateFilterInputProps {
    /**
     * Notifies the parent whenever the resolved filter changes. Called with
     * the resolved `{ createdStart?, label? }` when the inputs form a valid
     * filter (≥1 constraint), and with `null` otherwise. Rev 13: the browser
     * performs NO subject-search traversal — the job's server-side gathering
     * phase resolves the filter after submission (PRD §Filter subject
     * discovery).
     */
    onFilterChanged: (filter: ResolvedFilter | null) => void;
}

/**
 * Filter-mode subject-selection input (PRD-001 rev 13).
 *
 * Accepts two independently-optional constraints — at least one required:
 *   (1) Date + time (minute precision) + a timezone selector (defaults to the
 *       browser TZ; full IANA list behind a "Change" expansion). Resolved to
 *       an RFC 3339 UTC instant client-side (`filterInputs.ts`).
 *   (2) A single free-text label, submitted verbatim.
 *
 * There is no "fetch matching subjects" action (removed in rev 13): the form
 * summary is the resolved filter itself, and the matched count first appears
 * on the job's history entry once server-side gathering completes. A
 * persistent note sets the >100k confirmation expectation (PRD §Confirmation
 * expectation is set at submit time).
 *
 * The UI does not persist any filter inputs to localStorage / sessionStorage;
 * the filter inputs are treated as PHI-adjacent (agent-smith IMPORTANT I2 — "a
 * UUID + a date range in project X is a re-identification risk"; the free-text
 * label is at least as identifying — a CSM could type a name/MRN fragment).
 * The label input carries `autoComplete="off"` so the browser does not retain
 * it in form history. Existing tools/no-localstorage-in-project.sh covers the
 * storage rule.
 */
export function DateFilterInput({ onFilterChanged }: DateFilterInputProps) {
    const browserTz = useMemo(() => detectBrowserTimeZone(), []);
    const [date, setDate] = useState<string>(""); // YYYY-MM-DD
    const [time, setTime] = useState<string>(""); // HH:MM (or HH:MM:SS — we trim)
    const [timeZone, setTimeZone] = useState<string>(browserTz);
    const [label, setLabel] = useState<string>(""); // free-text label (verbatim)
    const [tzPickerOpen, setTzPickerOpen] = useState(false);
    const [tzQuery, setTzQuery] = useState("");

    const parsedInputs = useMemo(
        () => parseFilterInputs({ date, time, timeZone, label }),
        [date, time, timeZone, label],
    );

    // Emit the resolved filter (or null) whenever it changes. The parent's
    // submit gate keys directly off this — no fetched-list staleness to
    // manage anymore.
    const onFilterChangedRef = useRef(onFilterChanged);
    onFilterChangedRef.current = onFilterChanged;
    useEffect(() => {
        if (!parsedInputs.ok) {
            onFilterChangedRef.current(null);
            return;
        }
        onFilterChangedRef.current({
            ...(parsedInputs.createdStartUtc ? { createdStart: parsedInputs.createdStartUtc } : {}),
            ...(parsedInputs.label ? { label: parsedInputs.label } : {}),
        });
    }, [parsedInputs]);

    const handleClear = () => {
        setDate("");
        setTime("");
        setLabel("");
    };

    // Bare timezone list rendered when the picker is opened, filtered by query.
    const tzOptions = useMemo(() => {
        const all = supportedTimeZones();
        if (!tzQuery.trim()) return all;
        const q = tzQuery.toLowerCase();
        return all.filter((tz) => tz.toLowerCase().includes(q));
    }, [tzQuery]);

    return (
        <div className="date-filter-input" data-testid="date-filter-input">
            <div className="callout callout-info" data-testid="date-filter-help">
                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                <span>
                    Find subjects in this project by a created-on-or-after moment and/or a
                    single label (set either or both — both must match). The job gathers the
                    matching subjects after you submit; the matched count appears on the job
                    history entry. Only the count is ever shown — no patient detail.
                </span>
            </div>
            <div className="date-filter-fields">
                <div className="field">
                    <label className="f-label" htmlFor="date-filter-date">
                        Date
                    </label>
                    <input
                        id="date-filter-date"
                        type="date"
                        className="f-input"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        data-testid="date-filter-date"
                    />
                </div>
                <div className="field">
                    <label className="f-label" htmlFor="date-filter-time">
                        Time (optional — defaults to 00:00)
                    </label>
                    <input
                        id="date-filter-time"
                        type="time"
                        step={60}
                        className="f-input"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        data-testid="date-filter-time"
                    />
                </div>
                <div className="field">
                    <label className="f-label">Timezone</label>
                    {tzPickerOpen ? (
                        <div
                            data-testid="date-filter-tz-picker"
                            style={{
                                background: "var(--n1)",
                                border: "1px solid var(--n3)",
                                borderRadius: "var(--r)",
                                padding: 10,
                                maxWidth: 340,
                            }}
                        >
                            <input
                                type="text"
                                className="f-input"
                                placeholder="Search timezones…"
                                value={tzQuery}
                                onChange={(e) => setTzQuery(e.target.value)}
                                data-testid="date-filter-tz-query"
                                autoFocus
                                style={{ marginBottom: 8 }}
                            />
                            <select
                                className="f-sel"
                                value={timeZone}
                                size={8}
                                onChange={(e) => {
                                    setTimeZone(e.target.value);
                                    setTzPickerOpen(false);
                                    setTzQuery("");
                                }}
                                data-testid="date-filter-tz-select"
                                style={{ width: "100%", marginBottom: 8 }}
                            >
                                {tzOptions.length === 0 ? (
                                    <option disabled>No matches</option>
                                ) : (
                                    tzOptions.map((tz) => (
                                        <option key={tz} value={tz}>
                                            {tz}
                                        </option>
                                    ))
                                )}
                            </select>
                            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                        setTimeZone(browserTz);
                                        setTzPickerOpen(false);
                                        setTzQuery("");
                                    }}
                                    data-testid="date-filter-tz-reset"
                                    title={`Reset to ${browserTz}`}
                                >
                                    <i className="fa-solid fa-rotate-left" aria-hidden="true" /> Reset to default ({browserTz})
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                        setTzPickerOpen(false);
                                        setTzQuery("");
                                    }}
                                    data-testid="date-filter-tz-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div
                            data-testid="date-filter-tz-readout"
                            style={{ display: "flex", alignItems: "center", gap: 8 }}
                        >
                            <span
                                style={{ color: "var(--n9)", fontFamily: "ui-monospace, 'SF Mono', monospace", fontSize: 12 }}
                                data-testid="date-filter-tz-current"
                            >
                                {timeZone}
                            </span>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setTzPickerOpen(true)}
                                data-testid="date-filter-tz-override"
                            >
                                Change
                            </button>
                            {timeZone !== browserTz && (
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setTimeZone(browserTz)}
                                    data-testid="date-filter-tz-reset-inline"
                                    title={`Reset to ${browserTz}`}
                                >
                                    <i className="fa-solid fa-rotate-left" aria-hidden="true" /> Reset
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="date-filter-label-group">
                <div className="field">
                    <label className="f-label" htmlFor="date-filter-label">
                        Label
                    </label>
                    <input
                        id="date-filter-label"
                        type="text"
                        className="f-input"
                        placeholder="e.g. cohort-a"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        autoComplete="off"
                        data-testid="date-filter-label"
                    />
                </div>
            </div>

            {parsedInputs.ok && (
                <p
                    style={{ color: "var(--n7)", fontSize: 12 }}
                    data-testid="date-filter-resolved-utc"
                >
                    The job will gather subjects
                    {parsedInputs.createdStartUtc && (
                        <>
                            {" "}
                            created on or after <code>{parsedInputs.createdStartUtc}</code>
                        </>
                    )}
                    {parsedInputs.createdStartUtc && parsedInputs.label ? " and" : null}
                    {parsedInputs.label && (
                        <>
                            {" "}
                            with label <code>{parsedInputs.label}</code>
                        </>
                    )}
                </p>
            )}

            {(date || time || label) && (
                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClear}
                        data-testid="date-filter-clear"
                    >
                        Clear
                    </button>
                </div>
            )}

            <div className="callout callout-info" data-testid="date-filter-confirm-note">
                <i className="fa-solid fa-hand" aria-hidden="true" />
                <span>
                    Heads up: if your filter matches more than 100,000 subjects, the job will
                    pause on the <strong>job history</strong> page and wait for you to press{" "}
                    <strong>Continue</strong> before evaluations start (it cancels itself
                    after 7 days if never confirmed) — keep an eye on it after submitting.
                </span>
            </div>
        </div>
    );
}

/**
 * Best-available IANA timezone list. Uses `Intl.supportedValuesOf` when
 * present (Node 18+, modern browsers); falls back to a minimal common list.
 */
function supportedTimeZones(): string[] {
    type IntlWithSupported = typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    const intlExt = Intl as IntlWithSupported;
    if (typeof intlExt.supportedValuesOf === "function") {
        try {
            return intlExt.supportedValuesOf("timeZone");
        } catch {
            // fall through
        }
    }
    return ["UTC", "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York", "Europe/London", "Europe/Berlin", "Asia/Tokyo"];
}
